import { isAssistantHandleNamespace } from "./assistant-handles";

const ACCOUNT_HANDLE_PATTERN = /^[a-z0-9._]+$/;
const EVENT_SLUG_PATTERN = /^[a-z0-9_-]+$/;
const ONLY_NUMBERS_PATTERN = /^[0-9]+$/;
const CONSECUTIVE_PERIODS_PATTERN = /\.{2,}/;

export const USERNAME_MIN_LENGTH = 2;
export const USERNAME_MAX_LENGTH = 30;
export const HIIVE_SLUG_MIN_LENGTH = USERNAME_MIN_LENGTH;
export const HIIVE_SLUG_MAX_LENGTH = USERNAME_MAX_LENGTH;
export const EVENT_SLUG_MIN_LENGTH = 3;
export const EVENT_SLUG_MAX_LENGTH = 60;

// Username changes after onboarding (Instagram-shaped): two new names per
// rolling 14 days, and each name you leave stays reserved for you for 14
// days so you can switch back. Server rules: lib/identity/username-change.ts
export const USERNAME_CHANGES_PER_WINDOW = 2;
export const USERNAME_CHANGE_WINDOW_DAYS = 14;
export const USERNAME_HOLD_DAYS = 14;

export const RESERVED_HANDLES = new Set<string>([
  "admin",
  "apply",
  "support",
  "help",
  "status",
  "api",
  "login",
  "logout",
  "signup",
  "register",
  "settings",
  "me",
  "you",
  "profile",
  "profiles",
  "hiive",
  "hiives",
  "events",
  "event",
  "about",
  "terms",
  "privacy",
  "contact",
  "notifications",
]);

type HandleValidationOptions = {
  minLength?: number;
  maxLength?: number;
  allowReserved?: boolean;
};

export const DEFAULT_HANDLE_OPTIONS: HandleValidationOptions = {
  minLength: USERNAME_MIN_LENGTH,
  maxLength: USERNAME_MAX_LENGTH,
  allowReserved: false,
};

export function sanitizeHandle(input: string): string {
  const trimmed = input.trim().toLowerCase();
  const normalized = trimmed
    .replace(/[^a-z0-9._]/g, "_")
    .replace(/_+/g, "_")
    .replace(/\.+/g, ".")
    .replace(/^[_.]+/, "")
    .replace(/[_.]+$/, "");
  return normalized;
}

/**
 * Event URLs are human-readable slugs, not account handles. Preserve existing
 * hyphens and underscores so editing an event never silently changes its URL.
 */
export function sanitizeEventSlug(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/_+/g, "_")
    .replace(/^[-_]+|[-_]+$/g, "");
}

export function isReservedHandle(handle: string): boolean {
  return RESERVED_HANDLES.has(handle) || isAssistantHandleNamespace(handle);
}

/**
 * Granular validation result for username validation
 */
export type UsernameValidationResult =
  | { valid: true }
  | { valid: false; error: UsernameValidationError };

export type UsernameValidationError =
  | "too_short"
  | "too_long"
  | "invalid_characters"
  | "starts_with_period"
  | "ends_with_period"
  | "starts_with_underscore"
  | "ends_with_underscore"
  | "consecutive_periods"
  | "only_numbers"
  | "reserved";

export const USERNAME_ERROR_MESSAGES: Record<UsernameValidationError, string> =
  {
    too_short: "Too short",
    too_long: "Too long",
    invalid_characters: "Only letters, numbers, _ and .",
    starts_with_period: "Can't start with a period",
    ends_with_period: "Can't end with a period",
    starts_with_underscore: "Can't start with underscore",
    ends_with_underscore: "Can't end with underscore",
    consecutive_periods: "No consecutive periods",
    only_numbers: "Can't be only numbers",
    reserved: "This username is reserved",
  };

/**
 * Validates a username with granular error messages (Instagram-style rules)
 */
export function validateUsername(
  handle: string,
  options: HandleValidationOptions = DEFAULT_HANDLE_OPTIONS,
): UsernameValidationResult {
  const {
    minLength = USERNAME_MIN_LENGTH,
    maxLength = USERNAME_MAX_LENGTH,
    allowReserved = false,
  } = options;

  // Length checks
  if (handle.length < minLength) {
    return { valid: false, error: "too_short" };
  }
  if (handle.length > maxLength) {
    return { valid: false, error: "too_long" };
  }

  // Character pattern check
  if (!ACCOUNT_HANDLE_PATTERN.test(handle)) {
    return { valid: false, error: "invalid_characters" };
  }

  // Period position checks
  if (handle.startsWith(".")) {
    return { valid: false, error: "starts_with_period" };
  }
  if (handle.endsWith(".")) {
    return { valid: false, error: "ends_with_period" };
  }

  // Underscore position checks
  if (handle.startsWith("_")) {
    return { valid: false, error: "starts_with_underscore" };
  }
  if (handle.endsWith("_")) {
    return { valid: false, error: "ends_with_underscore" };
  }

  // Consecutive periods check
  if (CONSECUTIVE_PERIODS_PATTERN.test(handle)) {
    return { valid: false, error: "consecutive_periods" };
  }

  // Only numbers check
  if (ONLY_NUMBERS_PATTERN.test(handle)) {
    return { valid: false, error: "only_numbers" };
  }

  // Reserved handle check
  if (!allowReserved && isReservedHandle(handle)) {
    return { valid: false, error: "reserved" };
  }

  return { valid: true };
}

export function isValidHandle(
  handle: string,
  options: HandleValidationOptions = DEFAULT_HANDLE_OPTIONS,
): boolean {
  return validateUsername(handle, options).valid;
}

export function isValidHiiveSlug(slug: string): boolean {
  if (
    slug.length < HIIVE_SLUG_MIN_LENGTH ||
    slug.length > HIIVE_SLUG_MAX_LENGTH ||
    !ACCOUNT_HANDLE_PATTERN.test(slug)
  ) {
    return false;
  }
  return true;
}

export function isValidEventSlug(slug: string): boolean {
  if (
    slug.length < EVENT_SLUG_MIN_LENGTH ||
    slug.length > EVENT_SLUG_MAX_LENGTH ||
    !EVENT_SLUG_PATTERN.test(slug)
  ) {
    return false;
  }
  return true;
}

export function assertValidHandle(
  handle: string,
  options: HandleValidationOptions = DEFAULT_HANDLE_OPTIONS,
): void {
  if (!isValidHandle(handle, options)) {
    throw new Error("Invalid handle");
  }
}

export function assertValidHiiveSlug(slug: string): void {
  if (!isValidHiiveSlug(slug)) {
    throw new Error("Invalid hiive slug");
  }
}

export function assertValidEventSlug(slug: string): void {
  if (!isValidEventSlug(slug)) {
    throw new Error("Invalid event slug");
  }
}

export function ensureSanitizedHandle(
  input: string,
  options: HandleValidationOptions = DEFAULT_HANDLE_OPTIONS,
): string {
  const sanitized = sanitizeHandle(input);
  assertValidHandle(sanitized, options);
  return sanitized;
}

export function ensureSanitizedHiiveSlug(input: string): string {
  const sanitized = sanitizeHandle(input);
  if (!isValidHiiveSlug(sanitized)) {
    throw new Error("Invalid hiive slug");
  }
  return sanitized;
}

export function ensureSanitizedEventSlug(input: string): string {
  const sanitized = input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
  if (!isValidEventSlug(sanitized)) {
    throw new Error("Invalid event slug");
  }
  return sanitized;
}

export type SplitEventMention = {
  hiiveHandle: string;
  eventSlug: string;
};

export function splitEventMention(handle: string): SplitEventMention | null {
  const [hiiveHandle, eventSlug] = handle.split("/", 2);
  if (!hiiveHandle || !eventSlug) {
    return null;
  }
  if (!isValidHiiveSlug(hiiveHandle) || !isValidEventSlug(eventSlug)) {
    return null;
  }
  return { hiiveHandle, eventSlug };
}
